import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { firebaseService } from '../../services/firebaseService';
import AssinaturaModal from '../../components/cobrancas/AssinaturaModal';
import { Mensalidade } from '../../types/Mensalidade';
import { ArrowLeft, Edit, Printer, Trash2, DollarSign, Calendar, User, Settings } from 'lucide-react';
import { useNotification } from '../../hooks/useNotification';

interface AssinaturaDetalhesData {
  id: string;
  alunoId: string;
  alunoNome: string;
  valor: number;
  descricao: string;
  formaPagamento: string;
  proximoVencimento: string;
  status: string;
  ciclo: string;
  dataInicio: string;
  dataFim?: string;
  jurosMensal: number;
  multaPercentual: number;
  descontoPercentual: number;
  observacoes: string;
}


const AssinaturaDetalhes: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError, showConfirm, showInfo, NotificationComponent } = useNotification();
  const { userProfile } = useAuth();
  const [assinatura, setAssinatura] = useState<AssinaturaDetalhesData | null>(null);
  const [mensalidades, setMensalidades] = useState<Mensalidade[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showJurosModal, setShowJurosModal] = useState(false);

  const loadAssinaturaDetalhes = React.useCallback(async () => {
    if (!id || !userProfile?.empresaCnpj) return;
    
    try {
      setLoading(true);
      
      // Carregar dados da assinatura
      const assinaturaData = await firebaseService.getAssinatura(id, userProfile.empresaCnpj);
      if (!assinaturaData) {
        navigate('/cobrancas/assinaturas');
        return;
      }

      // Carregar dados do aluno
      const alunos = await firebaseService.getAlunos(userProfile.empresaCnpj);
      const aluno = alunos.find(a => a.id === assinaturaData.alunoId);

      setAssinatura({
        id: assinaturaData.id,
        alunoId: assinaturaData.alunoId,
        alunoNome: aluno?.nome || 'Cliente não encontrado',
        valor: assinaturaData.value,
        descricao: assinaturaData.description,
        formaPagamento: assinaturaData.billingType,
        proximoVencimento: assinaturaData.nextDueDate,
        status: assinaturaData.status,
        ciclo: assinaturaData.cycle,
        dataInicio: assinaturaData.createDate,
        dataFim: assinaturaData.deletedDate,
        jurosMensal: assinaturaData.interest || 0,
        multaPercentual: assinaturaData.fine || 0,
        descontoPercentual: assinaturaData.discount || 0,
        observacoes: assinaturaData.description || ''
      });

      // Buscar mensalidades reais do banco
      await loadMensalidades();
      
    } catch (error) {
      console.error('Erro ao carregar detalhes da assinatura:', error);
    } finally {
      setLoading(false);
    }
  }, [id, userProfile?.empresaCnpj, navigate]);

  useEffect(() => {
    if (id) {
      loadAssinaturaDetalhes();
    }
  }, [id, loadAssinaturaDetalhes]);

  const loadMensalidades = React.useCallback(async () => {
    if (!id || !userProfile?.empresaCnpj) return;
    
    try {
      // Buscar todas as mensalidades e filtrar pela assinatura
      const todasMensalidades = await firebaseService.getAllMensalidades(userProfile.empresaCnpj);
      const mensalidadesDaAssinatura = todasMensalidades.filter(m => m.assinaturaId === id);
      setMensalidades(mensalidadesDaAssinatura);
    } catch (error) {
      console.error('Erro ao carregar mensalidades:', error);
      // Se não existirem mensalidades, mostrar array vazio
      setMensalidades([]);
    }
  }, [id, userProfile?.empresaCnpj]);


  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pago':
        return 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20';
      case 'vencido':
        return 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20';
      case 'pendente':
        return 'text-yellow-600 dark:text-yellow-400 bg-yellow-50 dark:bg-yellow-900/20';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pago':
        return 'Pago';
      case 'vencido':
        return 'Vencido';
      case 'pendente':
        return 'Pendente';
      default:
        return status;
    }
  };

  const handleEditAssinatura = () => {
    setShowEditModal(true);
  };

  const handleEditJuros = () => {
    setShowJurosModal(true);
  };

  const handleDeleteAssinatura = async () => {
    if (!assinatura || !userProfile?.empresaCnpj) return;
    
    showConfirm(
      'Excluir Assinatura',
      `Tem certeza que deseja excluir a assinatura de ${assinatura.alunoNome}?`,
      async () => {
        try {
          await firebaseService.deleteAssinatura(userProfile.empresaCnpj, assinatura.id);
          showSuccess('Sucesso', 'Assinatura excluída com sucesso!');
          navigate('/cobrancas/assinaturas');
        } catch (error) {
          console.error('Erro ao excluir assinatura:', error);
          showError('Erro', 'Erro ao excluir assinatura. Tente novamente.');
        }
      },
      {
        confirmText: 'Excluir',
        cancelText: 'Cancelar'
      }
    );
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
    loadAssinaturaDetalhes();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!assinatura) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 text-lg">Assinatura não encontrada</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/cobrancas/assinaturas')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Voltar</span>
          </button>
          
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <span className="text-gray-500">Cobranças</span>
              </li>
              <li>
                <span className="text-gray-400">/</span>
              </li>
              <li>
                <span className="text-gray-500">Assinaturas</span>
              </li>
              <li>
                <span className="text-gray-400">/</span>
              </li>
              <li>
                <span className="text-gray-900 font-medium">Detalhes</span>
              </li>
            </ol>
          </nav>
        </div>

        <div className="flex items-center space-x-3">
          <button 
            onClick={handleEditAssinatura}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
          >
            <Edit className="h-4 w-4" />
            <span>Editar</span>
          </button>
          <button 
            onClick={() => showInfo('Informação', 'Funcionalidade de impressão será implementada em breve')}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
          >
            <Printer className="h-4 w-4" />
            <span>Imprimir carnê</span>
          </button>
          <button 
            onClick={handleDeleteAssinatura}
            className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4" />
            <span>Remover assinatura</span>
          </button>
        </div>
      </div>

      {/* Dados da Assinatura */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Dados da assinatura</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Cliente</label>
            <div className="flex items-center space-x-3">
              <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <span className="text-green-600 text-sm font-medium">
                  {assinatura.alunoNome.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-sm font-medium text-gray-900">{assinatura.alunoNome}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Valor</label>
            <div className="text-2xl font-bold text-gray-900">{formatCurrency(assinatura.valor)}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Forma de pagamento</label>
            <div className="text-sm text-gray-900">{assinatura.formaPagamento}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Próxima cobrança</label>
            <div className="text-sm text-gray-900">{formatDate(assinatura.proximoVencimento)}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Intervalo</label>
            <div className="text-sm text-gray-900">{assinatura.ciclo}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data de fim da assinatura</label>
            <div className="text-sm text-gray-900">
              {assinatura.dataFim ? formatDate(assinatura.dataFim) : 'Não definida'}
            </div>
          </div>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Descrição</label>
          <div className="text-sm text-gray-900 bg-gray-50 p-3 rounded-md">
            {assinatura.descricao}
          </div>
          <p className="text-xs text-gray-500 mt-1">A descrição informada será impressa no boleto.</p>
        </div>
      </div>

      {/* Juros, Multa e Desconto */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Juros, multa e Desconto</h2>
          <button 
            onClick={handleEditJuros}
            className="flex items-center space-x-2 px-4 py-2 border border-primary-300 text-primary-600 rounded-md hover:bg-primary-50"
          >
            <Edit className="h-4 w-4" />
            <span>Editar</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-md font-medium text-gray-900 mb-4">Juros e multa</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Juros ao mês</label>
                <div className="text-sm text-gray-900">{assinatura.jurosMensal}%</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Multa por atraso</label>
                <div className="text-sm text-gray-900">{assinatura.multaPercentual}%</div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-md font-medium text-gray-900 mb-4">Desconto</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Desconto</label>
                <div className="text-sm text-gray-900">{assinatura.descontoPercentual}%</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prazo máximo do desconto</label>
                <div className="text-sm text-gray-900">Até o dia do vencimento</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mensalidades Geradas */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recorrentes (Assinaturas)</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status do Pagamento
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Forma de pagamento
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data de vencimento
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {mensalidades.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    Nenhuma mensalidade encontrada para esta assinatura
                  </td>
                </tr>
              ) : (
                mensalidades.map((mensalidade) => (
                <tr key={mensalidade.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-3">
                        <span className="text-green-600 text-sm font-medium">
                          {assinatura.alunoNome.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-900">{assinatura.alunoNome}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-blue-600">
                      {formatCurrency(mensalidade.valor)}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      mensalidade.status === 'pago' 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                        : mensalidade.status === 'pendente'
                        ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                        : mensalidade.status === 'vencido'
                        ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                    }`}>
                      {mensalidade.status === 'pago' ? 'Pago' : 
                       mensalidade.status === 'pendente' ? 'Pendente' :
                       mensalidade.status === 'vencido' ? 'Vencido' : 
                       mensalidade.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{assinatura.formaPagamento}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <div className="text-sm text-gray-900">{formatDate(mensalidade.dataVencimento)}</div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(mensalidade.status)}`}>
                        {getStatusText(mensalidade.status)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button 
                        className="text-primary-600 hover:text-primary-900"
                        title="Receber pagamento"
                      >
                        <DollarSign className="h-4 w-4" />
                      </button>
                      <button 
                        className="text-primary-600 hover:text-primary-900"
                        title="Renovar cobrança"
                      >
                        <Calendar className="h-4 w-4" />
                      </button>
                      <button 
                        className="text-primary-600 hover:text-primary-900"
                        title="Gerar nota fiscal"
                      >
                        <Settings className="h-4 w-4" />
                      </button>
                      <button 
                        className="text-primary-600 hover:text-primary-900"
                        title="Editar mensalidade"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button 
                        className="text-primary-600 hover:text-primary-900"
                        title="Imprimir boleto"
                      >
                        <Printer className="h-4 w-4" />
                      </button>
                      <button 
                        className="text-primary-600 hover:text-primary-900"
                        title="Enviar por email"
                      >
                        <User className="h-4 w-4" />
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-900"
                        title="Excluir mensalidade"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Edição da Assinatura */}
      <AssinaturaModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        assinaturaId={id}
        onSuccess={handleEditSuccess}
      />

      {/* Modal de Edição de Juros (placeholder) */}
      {showJurosModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Editar Juros, Multa e Desconto
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Juros ao mês (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  defaultValue={assinatura?.jurosMensal || 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Multa por atraso (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  defaultValue={assinatura?.multaPercentual || 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Desconto (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  defaultValue={assinatura?.descontoPercentual || 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowJurosModal(false)}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  showInfo('Informação', 'Funcionalidade de atualização será implementada em breve');
                  setShowJurosModal(false);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      <NotificationComponent />
    </div>
  );
};

export default AssinaturaDetalhes;
